/**
 * Cart Page Shipping Protection Guard
 *
 * The cart and checkout validation function rejects any cart whose only
 * contents are Shipping Protection. It enforces this on every cart mutation,
 * not just at checkout: POSTs to /cart/add.js and /cart/change.js that would
 * leave a protection-only cart return a 422.
 *
 * On the /cart page this surfaced as a confusing bug: a buyer with Shipping
 * Protection + one product who removed that product hit the 422, so the removal
 * was rejected — the product stayed in the cart and only a console error showed.
 * (The cart drawer renders <shipping-protection>, which manages this; the
 * standalone /cart page does not.)
 *
 * This guard makes the shared CartStateManager protection-aware on the cart
 * page. When a quantity change would leave Shipping Protection as the only item,
 * it instead issues a single bulk /cart/update.js that also removes protection,
 * so the cart empties cleanly in one allowed operation. It also clears a cart
 * that is already protection-only on load (e.g. one built before the function
 * existed). The validation function remains the authoritative server-side
 * enforcement; this is purely the matching front-end UX.
 */
(function () {
  "use strict";

  // Identify Shipping Protection the same way the validation function does:
  // product handle is the primary, stable signal; variant id and SKU are
  // belt-and-suspenders in case the handle ever changes.
  var PROTECTION_HANDLE = "shipping-protection";
  var PROTECTION_VARIANT_ID = 43628919587015;
  var PROTECTION_SKU = "60044";

  function isShippingProtection(item) {
    if (!item) return false;
    return (
      item.handle === PROTECTION_HANDLE ||
      item.variant_id === PROTECTION_VARIANT_ID ||
      item.sku === PROTECTION_SKU
    );
  }

  // True only when the cart has at least one line and every line is Shipping
  // Protection — the exact condition the validation function rejects.
  function isProtectionOnly(items) {
    return (
      Array.isArray(items) &&
      items.length > 0 &&
      items.every(isShippingProtection)
    );
  }

  function root() {
    return (
      (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || "/"
    );
  }

  // Resolve the cart line a CartStateManager.updateQuantity target refers to.
  // target is either a line-item key ("variantId:hash") or a 1-based line index.
  function resolveItem(items, target) {
    if (typeof target === "string" && target.indexOf(":") !== -1) {
      var byKey = items.find(function (i) { return i.key === target; });
      if (byKey) return byKey;
      var variantId = target.split(":")[0];
      return (
        items.find(function (i) { return String(i.variant_id) === variantId; }) ||
        null
      );
    }
    var idx = parseInt(target, 10);
    if (!isNaN(idx) && idx >= 1 && idx <= items.length) return items[idx - 1];
    return null;
  }

  // Remaining lines (quantity > 0) after applying the requested change.
  function simulate(items, changedItem, quantity) {
    var q = parseInt(quantity, 10);
    return items
      .map(function (i) {
        return i === changedItem ? Object.assign({}, i, { quantity: q }) : i;
      })
      .filter(function (i) { return i.quantity > 0; });
  }

  var clearing = false;

  async function bulkClear(manager, extraUpdates) {
    var items = manager.getState().items || [];
    var updates = Object.assign({}, extraUpdates);
    items.forEach(function (i) {
      if (isShippingProtection(i)) updates[i.variant_id] = 0;
    });

    var res = await fetch(root() + "cart/update.js", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ updates: updates }),
    });
    if (!res.ok) throw new Error("cart/update.js failed: " + res.status);

    // Reload to render the resulting (empty) cart page cleanly rather than
    // relying on a partial section re-render of an emptied cart.
    window.location.reload();
  }

  function install(manager) {
    if (!manager || manager.__protectionGuardInstalled) return false;
    manager.__protectionGuardInstalled = true;

    var original = manager.updateQuantity.bind(manager);

    manager.updateQuantity = function (target, quantity) {
      try {
        var items = manager.getState().items || [];
        var changed = resolveItem(items, target);
        if (changed) {
          var remaining = simulate(items, changed, quantity);
          if (isProtectionOnly(remaining)) {
            var extra = {};
            extra[changed.variant_id] = parseInt(quantity, 10);
            return bulkClear(manager, extra);
          }
        }
      } catch (e) {
        console.error("ShippingProtectionGuard: pre-check failed", e);
      }
      return original(target, quantity);
    };

    clearIfProtectionOnly(manager);
    manager.subscribe(function (state) {
      clearIfProtectionOnly(manager, state);
    });

    return true;
  }

  async function clearIfProtectionOnly(manager, state) {
    if (clearing) return;
    var items = (state && state.items) || manager.getState().items || [];
    if (!isProtectionOnly(items)) return;
    clearing = true;
    try {
      await bulkClear(manager, {});
    } catch (e) {
      console.error("ShippingProtectionGuard: failed to clear protection-only cart", e);
      clearing = false;
    }
  }

  // CartStateManager loads in the document head; this guard loads in the cart
  // section body. Both are deferred, but poll briefly in case ordering differs.
  if (!install(window.cartStateManager)) {
    var attempts = 0;
    var poll = setInterval(function () {
      attempts += 1;
      if (install(window.cartStateManager) || attempts >= 50) clearInterval(poll);
    }, 100);
  }
})();
